import React from 'react'
import styled from 'styled-components'
import { accents, brand, gray } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'
import { spacing } from 'sutra/tokens/spacing'

import { SENSITIVITY_OPTIONS } from 'features/trend-detection/constants'

export interface SensitivityControlProps {
  value: string
  onChange: (value: string) => void
}

/** Radio-card row for Issue Sensitivity. Sutra has no Radio primitive, so
 * this is local — three equal-width cards instead of the old joined
 * segmented control. Each card always shows its own description as a
 * caption (no more single shared caption that only reveals the selected
 * option's description on click). */
export const SensitivityControl: React.FC<SensitivityControlProps> = ({
  value,
  onChange,
}) => (
  <Wrapper>
    <Group role="radiogroup" aria-label="Issue Sensitivity">
      {SENSITIVITY_OPTIONS.map((opt) => (
        <Card
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={value === opt.value}
          $active={value === opt.value}
          onClick={() => onChange(opt.value)}
        >
          <Row>
            <Dot $active={value === opt.value} />
            <Label>{opt.label}</Label>
          </Row>
          <Caption>{opt.tooltip}</Caption>
          <DotsRow aria-hidden="true">
            {[1, 2, 3].map((i) => (
              <LevelDot key={i} $filled={i <= opt.volumeDots} />
            ))}
          </DotsRow>
        </Card>
      ))}
    </Group>
  </Wrapper>
)

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]}px;
`

const Group = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${spacing[2]}px;
`

const Card = styled.button<{ $active: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]}px;
  padding: ${spacing[3]}px;
  border-radius: ${radii.sm}px;
  text-align: left;
  cursor: pointer;
  border: 1px solid
    ${({ $active }) => ($active ? accents.reflectionBlue[500] : gray[200])};
  background: ${({ $active }) =>
    $active ? accents.reflectionBlue[50] : brand.white};
  transition:
    background ${motion.duration.fast}ms ${motion.ease},
    border-color ${motion.duration.fast}ms ${motion.ease};

  &:hover {
    border-color: ${({ $active }) =>
      $active ? accents.reflectionBlue[500] : gray[300]};
    background: ${({ $active }) =>
      $active ? accents.reflectionBlue[50] : gray[50]};
  }

  &:focus-visible {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: 2px;
  }
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]}px;
`

const Dot = styled.span<{ $active: boolean }>`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: ${radii.pill}px;
  border: 2px solid
    ${({ $active }) => ($active ? accents.reflectionBlue[500] : gray[300])};
  background: ${({ $active }) =>
    $active ? accents.reflectionBlue[500] : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: ${radii.pill}px;
    background: ${brand.white};
    opacity: ${({ $active }) => ($active ? 1 : 0)};
  }
`

const Label = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${gray[900]};
`

const Caption = styled.span`
  flex: 1;
  font-size: 12px;
  color: ${gray[500]};
`

const DotsRow = styled.span`
  display: flex;
  flex-shrink: 0;
  gap: 4px;
  margin-top: ${spacing[2]}px;
  padding-top: ${spacing[2]}px;
  border-top: 1px solid ${gray[200]};
`

const LevelDot = styled.span<{ $filled: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: ${radii.pill}px;
  background: ${({ $filled }) =>
    $filled ? accents.reflectionBlue[500] : gray[200]};
`
